import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Download, Image as ImageIcon, Minimize2, Settings } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

interface CompressionStats {
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  savings: number;
}

export const ImageCompressTool: React.FC = () => {
  const { t } = useTranslation();
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [compressedImage, setCompressedImage] = useState<string | null>(null);
  const [quality, setQuality] = useState(0.8);
  const [maxWidth, setMaxWidth] = useState<number | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const [format, setFormat] = useState<'jpeg' | 'webp' | 'png'>('jpeg');
  const [stats, setStats] = useState<CompressionStats | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(t('tools.imageCompress.invalidImageError'));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
        setCompressedImage(null);
        setStats(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const compressImage = async () => {
    if (!originalImage || !canvasRef.current) return;

    setIsProcessing(true);
    
    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        
        if (maxWidth && width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (maxHeight && height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw and compress image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Get compressed image data
        const mimeType = format === 'png' ? 'image/png' : `image/${format}`;
        const compressedDataUrl = canvas.toDataURL(mimeType, quality);
        
        setCompressedImage(compressedDataUrl);
        
        // Calculate compression stats
        const originalSize = dataURLtoBlob(originalImage).size;
        const compressedSize = dataURLtoBlob(compressedDataUrl).size;
        const compressionRatio = (compressedSize / originalSize) * 100;
        const savings = ((originalSize - compressedSize) / originalSize) * 100;
        
        setStats({
          originalSize,
          compressedSize,
          compressionRatio,
          savings
        });
        
        setIsProcessing(false);
      };
      
      img.src = originalImage;
    } catch (error) {
      console.error('Compression error:', error);
      toast.error(t('tools.imageCompress.compressionError'));
      setIsProcessing(false);
    }
  };

  const dataURLtoBlob = (dataURL: string): Blob => {
    const arr = dataURL.split(',');
    const mime = arr[0].match(/:(.*?);/)![1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const downloadCompressed = () => {
    if (!compressedImage) return;
    
    const blob = dataURLtoBlob(compressedImage);
    const extension = format === 'jpeg' ? 'jpg' : format;
    saveAs(blob, `compressed-image.${extension}`);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const presetQualities = [
    { name: t('tools.imageCompress.qualityPresets.high'), value: 0.9 },
    { name: t('tools.imageCompress.qualityPresets.medium'), value: 0.7 },
    { name: t('tools.imageCompress.qualityPresets.low'), value: 0.5 },
    { name: t('tools.imageCompress.qualityPresets.veryLow'), value: 0.3 },
  ];

  const presetSizes = [
    { name: t('tools.imageCompress.sizePresets.hd'), width: 1920, height: 1080 },
    { name: t('tools.imageCompress.sizePresets.fullHd'), width: 1920, height: 1080 },
    { name: t('tools.imageCompress.sizePresets.hdReady'), width: 1280, height: 720 },
    { name: t('tools.imageCompress.sizePresets.socialMedia'), width: 1200, height: 630 },
    { name: t('tools.imageCompress.sizePresets.instagramSquare'), width: 1080, height: 1080 },
    { name: t('tools.imageCompress.sizePresets.profilePicture'), width: 400, height: 400 },
  ];

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Minimize2 className="w-5 h-5" />
            <span>{t('tools.imageCompress.name')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {t('common.dragDropImage')}
              </p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
              >
                <Upload className="w-4 h-4 mr-2" />
                {t('common.selectImage')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compression Settings */}
      {originalImage && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Settings className="w-5 h-5" />
              <span>{t('tools.imageCompress.compressionSettings')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('tools.imageCompress.outputFormat')}
                  </label>
                  <select
                    value={format}
                    onChange={(e) => setFormat(e.target.value as 'jpeg' | 'webp' | 'png')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="jpeg">{t('tools.imageCompress.formats.jpeg')}</option>
                    <option value="webp">{t('tools.imageCompress.formats.webp')}</option>
                    <option value="png">{t('tools.imageCompress.formats.png')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('tools.imageCompress.quality')}: {Math.round(quality * 100)}%
                  </label>
                  <input
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.1"
                    value={quality}
                    onChange={(e) => setQuality(parseFloat(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{t('tools.imageCompress.smallerFile')}</span>
                    <span>{t('tools.imageCompress.betterQuality')}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('tools.imageCompress.maxWidth')}
                    </label>
                    <Input
                      type="number"
                      value={maxWidth || ''}
                      onChange={(e) => setMaxWidth(e.target.value ? parseInt(e.target.value) : null)}
                      placeholder={t('common.auto')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('tools.imageCompress.maxHeight')}
                    </label>
                    <Input
                      type="number"
                      value={maxHeight || ''}
                      onChange={(e) => setMaxHeight(e.target.value ? parseInt(e.target.value) : null)}
                      placeholder={t('common.auto')}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('tools.imageCompress.qualityPresetsTitle')}
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {presetQualities.map((preset) => (
                      <Button
                        key={preset.name}
                        onClick={() => setQuality(preset.value)}
                        variant={quality === preset.value ? 'primary' : 'outline'}
                        size="sm"
                      >
                        {preset.name}
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('tools.imageCompress.sizePresetsTitle')}
                  </h4>
                  <div className="space-y-1">
                    {presetSizes.map((preset) => (
                      <Button
                        key={preset.name}
                        onClick={() => {
                          setMaxWidth(preset.width);
                          setMaxHeight(preset.height);
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full text-left justify-start"
                      >
                        <span className="text-xs">
                          {preset.name} ({preset.width}×{preset.height})
                        </span>
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <Button
                onClick={compressImage}
                disabled={isProcessing}
                variant="primary"
                className="w-full"
              >
                {isProcessing ? t('tools.imageCompress.compressing') : t('tools.imageCompress.compressImage')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {originalImage && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('tools.imageCompress.originalImage')}</CardTitle>
            </CardHeader>
            <CardContent>
              <img
                src={originalImage}
                alt="Original"
                className="w-full h-auto max-h-64 object-contain rounded border"
              />
            </CardContent>
          </Card>

          {compressedImage && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{t('tools.imageCompress.compressedImage')}</span>
                  <Button
                    onClick={downloadCompressed}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('common.download')}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <img
                  src={compressedImage}
                  alt="Compressed"
                  className="w-full h-auto max-h-64 object-contain rounded border"
                />
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Compression Stats */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle>{t('tools.imageCompress.compressionStatistics')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {formatFileSize(stats.originalSize)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.imageCompress.originalSize')}</div>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {formatFileSize(stats.compressedSize)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.imageCompress.compressedSize')}</div>
              </div>
              <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {stats.compressionRatio.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.imageCompress.finalSize')}</div>
              </div>
              <div className="text-center p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {stats.savings.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">{t('tools.imageCompress.spaceSaved')}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};
